import {
  ArrowLeft,
  ArrowsInSimple,
  ArrowSquareOut,
} from "@phosphor-icons/react";
import { Badge, Button, Flex, Spinner, Text } from "@radix-ui/themes";
import { useCallback, useRef, useState } from "react";

import { AssetViewerPane, type AssetFile } from "./AssetViewerPane";

import { useTicketViewData } from "../hooks/useTicketView";

interface TicketViewPaneProps {
  ticketId: string;
  onDismiss: () => void;
  onCollapse?: () => void;
  onBackToProject?: () => void;
  reviewStatus?: string | null;
}

const STATUS_COLORS: Record<
  string,
  "green" | "orange" | "blue" | "gray" | "red"
> = {
  approved: "green",
  complete: "green",
  needs_review: "orange",
  wip: "blue",
  incomplete: "red",
};

const STATUS_LABELS: Record<string, string> = {
  approved: "Approved",
  complete: "Complete",
  needs_review: "Needs Review",
  wip: "In Progress",
  incomplete: "Incomplete",
};

export function TicketViewPane({
  ticketId,
  onDismiss,
  onCollapse,
  onBackToProject,
  reviewStatus,
}: TicketViewPaneProps) {
  const { ticket, comments, isLoading } = useTicketViewData(ticketId);

  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(
    null,
  );
  const [highlightPing, setHighlightPing] = useState<{
    x: number;
    y: number;
    page?: number;
  } | null>(null);
  const focusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCommentClick = useCallback(
    (comment: {
      _id?: string;
      rel_x?: number;
      rel_y?: number;
      page_num?: number;
    }) => {
      setSelectedCommentId(comment._id ?? null);
      if (comment.rel_x != null && comment.rel_y != null) {
        if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
        setHighlightPing({
          x: comment.rel_x,
          y: comment.rel_y,
          page: comment.page_num,
        });
        focusTimerRef.current = setTimeout(() => setHighlightPing(null), 3000);
      }
    },
    [],
  );

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-base-white">
        <Flex direction="column" align="center" gap="2">
          <Spinner size="3" />
          <Text size="2" className="text-puntt-neutral-gray-9">
            Loading ticket...
          </Text>
        </Flex>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="flex h-full items-center justify-center bg-base-white">
        <Text size="2" className="text-puntt-neutral-gray-9">
          Ticket not found
        </Text>
      </div>
    );
  }

  const status = ticket.status ?? "wip";
  const latestBoard = ticket.revisionBoards?.filter((b) => !b.deletedAt).at(-1);
  const reviewFiles = latestBoard?.reviewFiles ?? [];
  const thumbnails = ticket.thumbnails ?? [];

  const assets: AssetFile[] =
    reviewFiles.length > 0
      ? reviewFiles
      : thumbnails.map((src, i) => ({
          source: src,
          name: `Page ${i + 1}`,
        }));

  return (
    <div
      className="flex h-full flex-col bg-base-white"
      data-testid="ticket-view-pane"
    >
      {/* Header */}
      <Flex
        align="center"
        justify="between"
        className="shrink-0 border-b border-puntt-neutral-gray-5 px-4 py-3"
      >
        <Flex align="center" gap="2" className="min-w-0 flex-1">
          {onBackToProject && (
            <Button
              size="1"
              variant="ghost"
              onClick={onBackToProject}
              className="shrink-0 cursor-pointer"
              aria-label="Back to project"
            >
              <ArrowLeft size={14} />
            </Button>
          )}
          <Text
            size="2"
            weight="bold"
            className="truncate text-puntt-neutral-gray-12"
          >
            {ticket.title}
          </Text>
          <Badge
            size="1"
            variant="soft"
            color={STATUS_COLORS[status] ?? "gray"}
          >
            {STATUS_LABELS[status] ?? status}
          </Badge>
          {latestBoard?.isAIReviewed && (
            <Badge size="1" variant="soft" color="blue">
              AI Reviewed
            </Badge>
          )}
          {reviewStatus && (
            <Text size="1" className="animate-pulse italic text-puntt-accent-9">
              {reviewStatus}
            </Text>
          )}
        </Flex>
        <Flex align="center" gap="1" className="shrink-0">
          <Button size="1" variant="ghost" asChild className="cursor-pointer">
            <a
              href={`/tickets/${ticketId}/view?version=0`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ArrowSquareOut size={14} />
            </a>
          </Button>
          {onCollapse && (
            <Button
              size="1"
              variant="ghost"
              onClick={onCollapse}
              className="cursor-pointer"
              aria-label="Collapse pane"
            >
              <ArrowsInSimple size={14} />
            </Button>
          )}
          <Button
            size="1"
            variant="ghost"
            onClick={onDismiss}
            className="cursor-pointer"
            aria-label="Close ticket view"
          >
            <ArrowSquareOut size={14} style={{ transform: "rotate(180deg)" }} />
          </Button>
        </Flex>
      </Flex>

      {/* Body */}
      <div className="min-h-0 flex-1">
        <AssetViewerPane
          title=""
          assets={assets}
          comments={comments ?? []}
          activeCommentId={selectedCommentId}
          onCommentClick={handleCommentClick}
          onPinClick={handleCommentClick}
          highlightPosition={highlightPing}
          ticketId={ticketId}
        />
      </div>
    </div>
  );
}
